<template>
    <div class="fb-sidebar">
        <div class="fb-sidebar-header">
            <div class="fb-sidebar-header-icon">
                <i class="fal fa-plus-circle"></i>
            </div>
            <h3 class="fb-sidebar-title">
                {{ $t('form_builder.sidebar.title') }}
            </h3>
        </div>
        <div class="fb-sidebar-search">
            <div class="fb-sidebar-search-wrap">
                <i class="fal fa-search fb-sidebar-search-icon"></i>
                <input
                    v-model="search"
                    type="text"
                    :placeholder="$t('form_builder.sidebar.search')"
                />
            </div>
        </div>
        <div class="fb-sidebar-list">
            <div
                v-for="group in filteredGroups"
                :key="group.key"
                class="fb-sidebar-group"
            >
                <div class="fb-sidebar-group-title">{{ group.label }}</div>
                <draggable
                    :list="group.fields"
                    :group="{ name: 'fields', pull: 'clone', put: false }"
                    :sort="false"
                    :clone="cloneField"
                    filter=".fb-sidebar-field--disabled"
                    :prevent-on-filter="false"
                    class="fb-sidebar-fields"
                >
                    <div
                        v-for="field in group.fields"
                        :key="field.key"
                        class="fb-sidebar-field"
                        :class="{ 'fb-sidebar-field--disabled': isFieldDisabled(field) }"
                        :title="isFieldDisabled(field) ? $t('form_builder.offline.requires_connection') : ''"
                    >
                        <div class="fb-sidebar-field-icon-wrap">
                            <i :class="field.icon" class="fb-sidebar-field-icon"></i>
                        </div>
                        <span class="fb-sidebar-field-label">{{ field.label }}</span>
                        <i
                            v-if="isFieldDisabled(field)"
                            class="fal fa-wifi-slash fb-sidebar-field-offline"
                        ></i>
                        <i v-else class="fal fa-grip-vertical fb-sidebar-field-grip"></i>
                    </div>
                </draggable>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import draggable from 'vuedraggable'
import { DEFAULT_RATING_MAX } from '@/config/rating'
import { DEFAULT_BANOC_LEVEL, DEFAULT_GEOLOCATION_PROVIDER } from '@/config/geolocation'

const FIELD_TYPES = [
    { typeId: 1, key: 'smalltext', label: 'Texte court', icon: 'fal fa-font', group: 'text' },
    { typeId: 3, key: 'textarea', label: 'Texte long', icon: 'fal fa-align-left', group: 'text' },
    { typeId: 4, key: 'markdown', label: 'Texte riche', icon: 'fal fa-paragraph', group: 'text' },
    { typeId: 16, key: 'email', label: 'Email', icon: 'fal fa-envelope', group: 'text' },
    { typeId: 5, key: 'number', label: 'Nombre', icon: 'fal fa-hashtag', group: 'text' },
    { typeId: 9, key: 'select', label: 'Liste déroulante', icon: 'fal fa-list-alt', group: 'choice' },
    { typeId: 10, key: 'radio', label: 'Boutons radio', icon: 'fal fa-dot-circle', group: 'choice' },
    { typeId: 11, key: 'checkbox', label: 'Cases à cocher', icon: 'fal fa-check-square', group: 'choice' },
    { typeId: 13, key: 'category', label: 'Catégorie', icon: 'fal fa-folder-tree', group: 'choice' },
    { typeId: 7, key: 'date', label: 'Date', icon: 'fal fa-calendar', group: 'datetime' },
    { typeId: 8, key: 'datetime', label: 'Date & heure', icon: 'fal fa-calendar-alt', group: 'datetime' },
    { typeId: 12, key: 'fileupload-images', label: 'Images', icon: 'fal fa-image', group: 'media', fileType: 'pictures' },
    { typeId: 12, key: 'fileupload-audio', label: 'Audio', icon: 'fal fa-headphones', group: 'media', fileType: 'audio' },
    { typeId: 12, key: 'fileupload-videos', label: 'Vidéos', icon: 'fal fa-video', group: 'media', fileType: 'videos' },
    { typeId: 12, key: 'fileupload-docs', label: 'Documents', icon: 'fal fa-file-pdf', group: 'media', fileType: 'files' },
    { typeId: 18, key: 'audio', label: 'Prise audio', icon: 'fal fa-microphone', group: 'media' },
    { typeId: 6, key: 'location', label: 'Localisation', icon: 'fal fa-map-marker-alt', group: 'advanced', mapType: 'osm' },
    { typeId: 6, key: 'location-banoc', label: 'Localisation BANOC', icon: 'fal fa-map-marked-alt', group: 'advanced', mapType: 'banoc' },
    { typeId: 19, key: 'geolocation-automatic', label: 'Géolocalisation automatique', icon: 'fal fa-location-arrow', group: 'advanced' },
    { typeId: 14, key: 'gdpr', label: 'RGPD', icon: 'fal fa-shield-alt', group: 'advanced' },
    { typeId: 17, key: 'rating', label: 'Notation', icon: 'fal fa-star', group: 'advanced' },
    { typeId: 2, key: 'hidden', label: 'Champ caché', icon: 'fal fa-eye-slash', group: 'advanced' },
    { typeId: 15, key: 'query', label: 'Requête', icon: 'fal fa-database', group: 'advanced', requiresConnection: true }
]

const GROUPS = [
    { key: 'text', label: 'Texte' },
    { key: 'choice', label: 'Choix' },
    { key: 'datetime', label: 'Date & Heure' },
    { key: 'media', label: 'Média' },
    { key: 'advanced', label: 'Avancé' }
]

/**
 * Config initiale d'un champ fraîchement déposé sur le canvas.
 * Les types sans réglage propre partent d'un objet vide.
 */
function defaultFieldConfig(field) {
    if (field.typeId === 18) return { maxDuration: 60, format: 'webm' }
    if (field.typeId === 19) return { provider: DEFAULT_GEOLOCATION_PROVIDER, level: DEFAULT_BANOC_LEVEL }
    if (field.typeId === 17) return { max: DEFAULT_RATING_MAX }
    if (field.typeId === 6) {
        return {
            map: {
                config: {
                    center: [48.8566, 2.3522],
                    zoom: 13,
                    type: field.mapType || 'osm',
                    carte: 'city',
                    restriction: { city: '', country: '' }
                },
                pois: [],
                restrict: { cities: [] }
            }
        }
    }
    if (field.fileType) return { type: field.fileType, max: 5 }
    return {}
}

let fieldUid = 0

export default {
    name: 'FormBuilderSidebar',
    components: { draggable },
    props: {
        availableOffline: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            search: ''
        }
    },
    computed: {
        filteredGroups() {
            const q = this.search.toLowerCase()
            return GROUPS.map((g) => ({
                ...g,
                fields: FIELD_TYPES.filter(
                    (f) => f.group === g.key && (!q || f.label.toLowerCase().includes(q))
                )
            })).filter((g) => g.fields.length > 0)
        }
    },
    methods: {
        // Fields that need the network (map tiles / geocoding, remote query options)
        // can't be filled offline, so they are disabled when the form is offline-capable.
        isFieldDisabled(field) {
            return this.availableOffline && !!field.requiresConnection
        },
        cloneField(field) {
            fieldUid++
            return {
                _uid: `new-${fieldUid}`,
                typeId: field.typeId,
                key: field.key,
                icon: field.icon,
                label: field.label,
                order: 0,
                translations: {
                    fr: { label: '', instruction: '', description: '', help: '' },
                    en: { label: '', instruction: '', description: '', help: '' }
                },
                config: defaultFieldConfig(field),
                requiredPublic: 0,
                requiredBackoffice: 0,
                hidden: 0,
                response_private: 0,
                requiredForWizard: 0
            }
        }
    }
}
</script>

<style scoped>
.fb-sidebar {
    width: 272px;
    background: #fff;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    height: 100%;
    flex-shrink: 0;
}

.fb-sidebar-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 20px 18px 0;
}

.fb-sidebar-header-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.fb-sidebar-header-icon i {
    color: var(--theme-primary, #3b82f6);
    font-size: 0.9rem;
}

.fb-sidebar-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

/* Search */
.fb-sidebar-search {
    padding: 14px 18px;
}

.fb-sidebar-search-wrap {
    position: relative;
}

.fb-sidebar-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.78rem;
    pointer-events: none;
}

.fb-sidebar-search input {
    width: 100%;
    padding: 9px 12px 9px 34px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.82rem;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;
    background: #f8fafc;
    transition: all 0.2s ease;
}

.fb-sidebar-search input::placeholder {
    color: #cbd5e1;
}

.fb-sidebar-search input:hover {
    border-color: #cbd5e1;
}

.fb-sidebar-search input:focus {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
    background: #fff;
}

/* List */
.fb-sidebar-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 18px 18px;
}

.fb-sidebar-group {
    margin-bottom: 20px;
}

.fb-sidebar-group-title {
    font-size: 0.68rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
    padding-left: 2px;
}

.fb-sidebar-fields {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

/* Field item */
.fb-sidebar-field {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border: 1.5px solid #f1f5f9;
    border-radius: 10px;
    cursor: grab;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    background: #fff;
}

.fb-sidebar-field:hover {
    border-color: #bfdbfe;
    background: #f0f7ff;
    transform: translateX(2px);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.08);
}

.fb-sidebar-field:active {
    cursor: grabbing;
    transform: scale(0.98);
}

.fb-sidebar-field-icon-wrap {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s ease;
}

.fb-sidebar-field:hover .fb-sidebar-field-icon-wrap {
    background: #dbeafe;
}

.fb-sidebar-field-icon {
    color: var(--theme-primary, #3b82f6);
    font-size: 0.8rem;
}

.fb-sidebar-field-label {
    font-size: 0.82rem;
    color: #334155;
    font-weight: 500;
    flex: 1;
}

.fb-sidebar-field-grip {
    color: #e2e8f0;
    font-size: 0.7rem;
    opacity: 0;
    transition: opacity 0.15s ease;
}

.fb-sidebar-field:hover .fb-sidebar-field-grip {
    opacity: 1;
}

/* Disabled (requires connection while form is offline-capable) */
.fb-sidebar-field--disabled,
.fb-sidebar-field--disabled:hover {
    cursor: not-allowed;
    opacity: 0.5;
    background: #f8fafc;
    border-color: #f1f5f9;
    transform: none;
    box-shadow: none;
}

.fb-sidebar-field--disabled .fb-sidebar-field-icon-wrap,
.fb-sidebar-field--disabled:hover .fb-sidebar-field-icon-wrap {
    background: #f1f5f9;
}

.fb-sidebar-field--disabled .fb-sidebar-field-icon {
    color: #94a3b8;
}

.fb-sidebar-field-offline {
    color: #94a3b8;
    font-size: 0.72rem;
    flex-shrink: 0;
}
</style>
